import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthContext";
import { useAnalytics } from "../../../firebase/hooks/useAnalytics";
import { useLanguageSync } from "../../../hooks/useLanguageSync";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import { UNFILED, openInChat } from "./historyComponents/historyService";
import { useFolders, useHistoryList, useSummaries, useTranscripts } from "./historyComponents/useHistory";
import { groupByDay } from "./historyComponents/historyFormat";
import { FolderRail } from "./historyComponents/components/FolderRail";
import { HistoryFilterBar } from "./historyComponents/components/HistoryFilterBar";
import { ConversationRow } from "./historyComponents/components/ConversationRow";
import { ConversationTranscript } from "./historyComponents/components/ConversationTranscript";
import { ConversationPrintView } from "./historyComponents/components/ConversationPrintView";
import { MoveToast } from "./historyComponents/components/MoveToast";
import { DeleteConfirmModal } from "./historyComponents/components/DeleteConfirmModal";
import { EmptyState, LoadingDots, SkeletonList } from "./historyComponents/components/HistoryStates";

// The History page. In historyComponents/: state lives in the hooks in
// useHistory.js, API calls in historyService.js, and the UI in components/.
// This file connects them.
function History() {
  const { currentUser, getUserProfile, isAuthReady } = useAuth();
  const navigate = useNavigate();
  const profile = getUserProfile();
  const { trackPageView, trackSearch } = useAnalytics();
  const { language, setLanguage } = useLanguageSync();

  const list = useHistoryList({ currentUser, language, trackSearch });
  const folders = useFolders({ currentUser, language, list });
  const summaries = useSummaries({ currentUser, language, list });
  const transcripts = useTranscripts({ currentUser, language, onError: list.setError });

  // The row being renamed, and the one waiting on the delete confirmation.
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    trackPageView("History Page");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect out if not signed in
  useEffect(() => {
    if (isAuthReady && !currentUser) {
      navigate({ pathname: "/login", search: `?lang=${language}` });
    }
  }, [currentUser, navigate, language, isAuthReady]);

  const openFolder = (folder) => {
    list.showFolder(folder);
    folders.resetMenu();
    transcripts.collapse();
  };

  const rename = (sessionId, title) => {
    setRenaming(null);
    list.rename(sessionId, title);
  };

  const confirmDelete = async () => {
    const sessionId = deleting;
    setDeleting(null);
    if (!sessionId) return;
    if (transcripts.expanded === sessionId) transcripts.collapse();
    if (await list.remove(sessionId)) folders.refresh();
  };

  const hasFilters = list.searchKeyword.trim() !== "" || list.filterDate !== "";
  const folderTitle = list.filterFolder === UNFILED
    ? (language === "zh" ? "未分類" : "Unfiled")
    : list.filterFolder;
  const viewTitle = folderTitle || (language === "zh" ? "所有對話" : "All conversations");
  const folderListProps = {
    folders: folders.folders,
    counts: folders.counts,
    total: folders.total,
    loading: folders.loading,
    error: folders.error,
    activeFolder: list.filterFolder,
    language,
    onOpen: openFolder,
    onRetry: folders.refresh,
  };
  const groups = groupByDay(list.items, language);

  // ── Loading state (auth / profile not ready) ──────────────────────────────
  if (!currentUser || !profile || !isAuthReady) {
    return (
      <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg text-black dark:text-white font-sans">
        <Sidebar language={language} />
        <LanguageSwitcher language={language} setLanguage={setLanguage} />
        <div className="flex-1 flex flex-col gap-6 lg:gap-8 items-center justify-center -mt-20 lg:-mt-36 px-4">
          <LoadingDots language={language} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg text-black dark:text-white font-sans">
      <Sidebar language={language} />
      <LanguageSwitcher language={language} setLanguage={setLanguage} />

      {/* The top padding lives on the inner column, not on this scroll
          container: `sticky top-0` sticks to the padding box, so padding here
          would leave a gap above the filter bar that rows scroll through. */}
      <div className="flex-1 flex flex-col items-center overflow-y-auto no-scrollbar">
        {/* Wider from xl, where the folder rail takes a column of its own. */}
        <div className="w-full lg:w-3/4 xl:w-full xl:max-w-6xl px-4 lg:px-8 pt-12 pb-6 lg:pb-10">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white">
              {language === "zh" ? "歷史紀錄" : "History"}
            </h1>
            <p className="flex justify-center text-sm text-gray-500 dark:text-gray-400 mt-1">
              {language === "zh" ? "查看您的歷史對話" : "View your chat history"}
            </p>
          </div>

          <div className="xl:grid xl:grid-cols-[13rem_minmax(0,1fr)] xl:gap-8">
            <FolderRail {...folderListProps} />

            <div className="min-w-0">
              {/* Below xl the folder button in the filter bar names the view, so
                  the heading is left to screen readers there. */}
              <div className="sr-only xl:not-sr-only xl:flex items-center gap-2 mb-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white break-words min-w-0">{viewTitle}</h2>
                {list.total > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300">
                    {list.total}
                  </span>
                )}
              </div>

              <HistoryFilterBar
                language={language}
                folderListProps={folderListProps}
                viewTitle={viewTitle}
                count={list.total}
                folderTitle={folderTitle}
                searchKeyword={list.searchKeyword}
                onSearch={list.setSearchKeyword}
                filterDate={list.filterDate}
                onDate={list.setFilterDate}
                hasFilters={hasFilters}
                onClear={list.clearFilters}
              />

              {list.error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm text-center">
                  {list.error}
                </div>
              )}

              {list.loading ? (
                <SkeletonList />
              ) : list.items.length === 0 ? (
                <EmptyState
                  language={language}
                  hasFilters={hasFilters}
                  folderName={folderTitle}
                  onBrowse={() => openFolder("")}
                  onNewChat={() => navigate({ pathname: "/tg-product-assistant", search: `?lang=${language}` })}
                  onClear={list.clearFilters}
                />
              ) : (
                <div className="flex flex-col">
                  {groups.map((group) => (
                    <section key={group.key} className="mb-5 last:mb-0">
                      <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2 px-1">
                        {group.label}
                      </h2>
                      <div className="flex flex-col gap-2">
                        {group.items.map((conv) => {
                          const id = conv.session_id;
                          const isExpanded = transcripts.expanded === id;
                          return (
                            <ConversationRow
                              key={id}
                              conv={conv}
                              language={language}
                              searchKeyword={list.searchKeyword}
                              showFolderChip={!list.filterFolder}
                              isExpanded={isExpanded}
                              onToggle={() => transcripts.toggle(id)}
                              isRenaming={renaming === id}
                              onStartRename={() => setRenaming(id)}
                              onCancelRename={() => setRenaming(null)}
                              onRename={(title) => rename(id, title)}
                              isExporting={transcripts.exporting.includes(id)}
                              onExport={(format) => transcripts.exportConversation(conv, format)}
                              onDelete={() => setDeleting(id)}
                              folderMenu={{
                                folders: folders.folders,
                                open: folders.menuFor === id,
                                busy: folders.moving,
                                error: folders.moveError,
                                onToggle: () => folders.toggleMenu(id),
                                onCancel: folders.closeMenu,
                                onSave: (folder) => folders.move(conv, folder),
                              }}
                            >
                              {isExpanded && (
                                <ConversationTranscript
                                  conv={conv}
                                  language={language}
                                  detail={transcripts.details[id]}
                                  summarizing={summaries.summarizing.includes(id)}
                                  onGenerateSummary={(force) => summaries.generate(id, force)}
                                  onContinue={() => openInChat(navigate, id, language, conv.agent_type)}
                                />
                              )}
                            </ConversationRow>
                          );
                        })}
                      </div>
                    </section>
                  ))}

                  {list.hasMore && (
                    <button
                      className="mt-3 self-center px-6 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium text-sm shadow-sm disabled:opacity-60"
                      onClick={list.loadMore}
                      disabled={list.loadingMore}
                    >
                      {list.loadingMore
                        ? language === "zh" ? "載入中…" : "Loading…"
                        : language === "zh" ? "載入更多" : "Load more"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <MoveToast
        notice={folders.notice}
        language={language}
        busy={folders.moving}
        onUndo={folders.undo}
        onOpen={() => {
          openFolder(folders.notice.folder || UNFILED);
          folders.dismissNotice();
        }}
        onDismiss={folders.dismissNotice}
      />

      {deleting && (
        <DeleteConfirmModal
          language={language}
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}

      {transcripts.printing && (
        <ConversationPrintView
          conv={transcripts.printing.conv}
          messages={transcripts.printing.messages}
          language={language}
          onDone={transcripts.donePrinting}
        />
      )}
    </div>
  );
}

export default History;
